import { bool, string, object, func } from 'prop-types';
import { useState, useEffect, useContext } from 'react';
import { QueryRenderer, graphql } from 'react-relay/legacy';
import { useIntl } from 'dibs-react-intl';
import { getCurrency } from 'dibs-intl/exports/units';
import { connect } from 'react-redux';
import classnames from 'classnames';

import { trackEvent } from 'dibs-tracking';
import Close from 'dibs-icons/exports/legacy/Close';
import { Link } from 'dibs-elements/exports/Link';

import { HeaderFooterServerVarsContext } from '../HeaderFooterServerVarsContext/HeaderFooterServerVarsContext';
import MessageBannerCarousel from './MessageBannerCarousel';
import { getIsFirstTimeVisitor } from './helpers/messageHelpers';
import { isPreview, getPreview, getPreviewVariables } from './helpers/previewHelpers';
import {
    getTrackingObj,
    isMessageBannerClosedManually,
    setIsBannerClosedManuallyInStorage,
    hasAnyMessagesByDevice,
} from './helpers/sharedMessageHelpers';
import { getTradeEOYMessageBanner, showTradeEOYMessage, getStatusInfo } from './TradeEOYMessaging';

import styles from './MessageBanner-style.scss';

/**
 * Messages for marketing banner can come from multiple sources and have the following precedence:
 * (1) Preview messages that pulled from a stringfied object on a URL query param
 * (2) Preview messages that are fetched with userType and/or setId from the URL
 * (3) Registered user messages that are fetched with userId
 * (4) Unregistered user messages that are fetched if user is not logged in
 */
function MessageBanner(props) {
    const [variables, setVariables] = useState({
        fireMessageBannerRefetch: false,
        isFirstTimeVisitor: false,
        hasRegisteredUserMessages: false,
        hasUnregisteredUserMessages: false,
        hasPreviewVariables: false,
        hasTradeUserId: false,
        previewSetId: '',
        previewUserType: '',
    });
    const intl = useIntl();
    const defaultCurrency = getCurrency(intl.locale);
    const { isMobile } = useContext(HeaderFooterServerVarsContext);
    const [previewMessagesFromUrl, setPreviewMessagesFromUrl] = useState(null);
    const [isBannerClosedManually, setIsBannerClosedManually] = useState(false);
    const [currentMessageTitle, setCurrentMessageTitle] = useState('');
    useEffect(() => {
        const { messages: previewMessages } = getPreview();
        if (previewMessages) {
            setPreviewMessagesFromUrl(previewMessages);
        }
    }, []);
    const {
        isClient,
        hasRootPendingRefetch,
        userId,
        hasUserId,
        isUserTypeTrade,
        useGlobalFallbackMessage = false,
        relayEnvironment,
        onBannerClose = () => {},
        hasTradeUserId,
    } = props;
    useEffect(() => {
        if (
            !variables.fireMessageBannerRefetch &&
            isClient &&
            !previewMessagesFromUrl &&
            !hasRootPendingRefetch
        ) {
            let nextVariables = {
                ...variables,
                userId,
                fireMessageBannerRefetch: true,
                isFirstTimeVisitor: !hasUserId && getIsFirstTimeVisitor(),
                hasRegisteredUserMessages: hasUserId,
                hasUnregisteredUserMessages: !hasUserId,
                hasPreviewVariables: false,
                previewSetId: '',
                previewUserType: '',
                hasTradeUserId,
            };

            const { userType: previewUserType, setId: previewSetId } = getPreviewVariables();

            if (previewUserType && previewSetId) {
                nextVariables = {
                    ...nextVariables,
                    hasPreviewVariables: true,
                    previewSetId,
                    previewUserType,
                };
            }
            setVariables(nextVariables);
        }
    }, [
        hasRootPendingRefetch,
        isClient,
        variables,
        previewMessagesFromUrl,
        userId,
        hasUserId,
        hasTradeUserId,
    ]);

    useEffect(() => {
        if (isClient) {
            setIsBannerClosedManually(isMessageBannerClosedManually());
        }
    }, [isClient]);

    const classes = classnames(styles.bannerContainer, { [styles.isTrade]: isUserTypeTrade });

    if (
        !isClient ||
        //Disable banner close on desktop and tablet
        (isBannerClosedManually && isMobile) ||
        (!variables.fireMessageBannerRefetch && !useGlobalFallbackMessage)
    ) {
        return null;
    }

    if (!variables.fireMessageBannerRefetch && !previewMessagesFromUrl) {
        return isMobile ? null : <div className={classes} />;
    }

    if (previewMessagesFromUrl) {
        return (
            <div className={classes}>
                <MessageBannerCarousel
                    viewer={null}
                    user={null}
                    messages={null}
                    showAllMessages
                    messagesToPreview={previewMessagesFromUrl}
                />
            </div>
        );
    }

    return (
        <QueryRenderer
            environment={relayEnvironment}
            variables={variables}
            query={graphql`
                query MessageBannerQuery(
                    $userId: String!
                    $hasRegisteredUserMessages: Boolean!
                    $hasUnregisteredUserMessages: Boolean!
                    $isFirstTimeVisitor: Boolean!
                    $hasPreviewVariables: Boolean!
                    $previewSetId: String!
                    $previewUserType: String!
                    $hasTradeUserId: Boolean!
                ) {
                    viewer {
                        ...MessageBannerCarousel_viewer
                        # using @include(if: $hasTradeUserId) on this fragment will throw an exception for logged out users
                        ...TradeEOYMessaging_viewer
                        activePromotion(userId: $userId) {
                            ...sharedMessageHelpers_activePromotion
                        }
                        expiringPromotion(userId: $userId) {
                            ...sharedMessageHelpers_expiringPromotion
                        }
                        user(userId: $userId) @include(if: $hasRegisteredUserMessages) {
                            ...MessageBannerCarousel_user
                            registeredUserMessages: bannerMessages {
                                ...MessageBannerCarousel_messages
                                ...sharedMessageHelpers_messages
                            }
                            profile @include(if: $hasTradeUserId) {
                                ...TradeEOYMessaging_profile
                            }
                            tradeFirmRewards @include(if: $hasTradeUserId) {
                                ...TradeEOYMessaging_tradeFirmRewards
                            }
                        }
                        unregisteredUserMessages: messageSet(
                            isFirstTimeVisitor: $isFirstTimeVisitor
                        ) @include(if: $hasUnregisteredUserMessages) {
                            ...MessageBannerCarousel_messages
                            ...sharedMessageHelpers_messages
                        }
                        previewMessages: messageSet(
                            setId: $previewSetId
                            userType: $previewUserType
                        ) @include(if: $hasPreviewVariables) {
                            ...MessageBannerCarousel_messages
                            ...sharedMessageHelpers_messages
                        }
                    }
                }
            `}
            render={({ error, props: renderProps }) => {
                if (error) {
                    return null;
                } else if (!renderProps) {
                    if (isMobile) {
                        return null;
                    } else {
                        return useGlobalFallbackMessage ? <div className={classes} /> : null;
                    }
                }

                const { viewer } = renderProps;
                const { user, activePromotion, expiringPromotion } = viewer;
                let messages = viewer?.previewMessages || null;
                let tradeEOYMessages = null;

                if (!isPreview()) {
                    const tradeEOY2020 = showTradeEOYMessage(viewer);
                    const status = getStatusInfo(
                        user?.profile,
                        user?.tradeFirmRewards,
                        defaultCurrency
                    );
                    if (status !== null && tradeEOY2020) {
                        tradeEOYMessages = getTradeEOYMessageBanner(intl, status); // add `as MessageConfig` once this is TSX
                        messages = null;
                    } else {
                        messages =
                            user?.registeredUserMessages ||
                            viewer?.unregisteredUserMessages ||
                            null;
                    }
                }

                if (
                    !useGlobalFallbackMessage &&
                    !hasAnyMessagesByDevice(messages, activePromotion, expiringPromotion)
                ) {
                    return null;
                }
                const closeIconClasses = classnames(styles.closeIcon, {
                    [styles.isTrade]: isUserTypeTrade,
                });

                return (
                    <div className={classes} data-tn="marketing-banner">
                        <MessageBannerCarousel
                            viewer={viewer}
                            user={user || null}
                            messages={messages}
                            tradeEOYMessages={tradeEOYMessages}
                            showAllMessages={variables.hasPreviewVariables}
                            useGlobalFallbackMessage={useGlobalFallbackMessage}
                            onMessageDisplay={({ messageTitle }) =>
                                setCurrentMessageTitle(messageTitle)
                            }
                        />
                        {/* Disable banner close on desktop and tablet */}
                        {!variables.hasPreviewVariables && isMobile && (
                            <Link
                                className={styles.closeIconWrapper}
                                onClick={() => {
                                    setIsBannerClosedManuallyInStorage();
                                    setIsBannerClosedManually(true);
                                    onBannerClose();

                                    trackEvent(
                                        getTrackingObj({
                                            action: 'marketing banner closed',
                                            label: currentMessageTitle,
                                        })
                                    );
                                }}
                                ariaLabel="Close"
                            >
                                <Close className={closeIconClasses} />
                            </Link>
                        )}
                    </div>
                );
            }}
        />
    );
}

MessageBanner.propTypes = {
    hasRootPendingRefetch: bool,
    hasUserId: bool,
    isClient: bool,
    isUserTypeTrade: bool,
    useGlobalFallbackMessage: bool,
    userId: string,
    relayEnvironment: object,
    onBannerClose: func,
    hasTradeUserId: bool,
};

const mapStateToProps = ({ header }) => {
    const { isUserTypeTrade } = header;

    return {
        isUserTypeTrade,
    };
};

export default connect(mapStateToProps)(MessageBanner);
